import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    const eventProductId = event.detail?.data?.productId ?? '';

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (eventProductId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML.
    // Primary lookup uses stable block-id; fallbacks handle template drift where block ids
    // can differ between live DOM and section-rendered HTML.
    const blockId = this.dataset.blockId;
    const productId = this.dataset.productId;
    let newProductPrice =
      blockId
        ? event.detail.data.html.querySelector(`product-price[data-block-id="${blockId}"]`)
        : null;

    if (!newProductPrice && productId) {
      newProductPrice = event.detail.data.html.querySelector(`product-price[data-product-id="${productId}"]`);
    }

    if (!newProductPrice) {
      const liveSection = this.closest('.shopify-section, dialog');
      if (liveSection instanceof HTMLElement) {
        const livePrices = Array.from(liveSection.querySelectorAll('product-price'));
        const liveIndex = livePrices.indexOf(this);

        if (liveIndex !== -1) {
          const sectionId = liveSection.id;
          const mirroredSection =
            sectionId
              ? event.detail.data.html.querySelector(`#${sectionId}`)
              : null;
          const mirroredPrices = mirroredSection?.querySelectorAll('product-price');
          const indexedMirror = mirroredPrices?.item(liveIndex);
          if (indexedMirror instanceof HTMLElement) {
            newProductPrice = indexedMirror;
          }
        }
      }
    }

    if (!newProductPrice) {
      const fallbackPrices = event.detail.data.html.querySelectorAll('product-price');
      if (fallbackPrices.length === 1) {
        newProductPrice = fallbackPrices.item(0);
      }
    }

    if (!newProductPrice) {
      return;
    }

    const priceContainer =
      this.refs.priceContainer instanceof HTMLElement ? this.refs.priceContainer : this.querySelector('[ref="priceContainer"]');
    const newPriceContainer = newProductPrice.querySelector('[ref="priceContainer"]');

    if (!(priceContainer instanceof HTMLElement) || !(newPriceContainer instanceof HTMLElement)) {
      this.innerHTML = newProductPrice.innerHTML;
      this.#notifyExternalPricingEngines();
      return;
    }

    // Update price container contents while preserving the DOM node (required for Affirm app block).
    priceContainer.innerHTML = newPriceContainer.innerHTML;

    // Update volume pricing note
    const currentNote = this.querySelector('[ref="volumePricingNote"]');
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!(newNote instanceof HTMLElement)) {
      currentNote?.remove();
    } else if (!(currentNote instanceof HTMLElement)) {
      priceContainer.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote.cloneNode(true));
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#notifyExternalPricingEngines();
  };

  #notifyExternalPricingEngines() {
    const detail = {
      productId: this.dataset.productId ?? '',
      blockId: this.dataset.blockId ?? '',
    };

    this.dispatchEvent(new CustomEvent('price:updated', { bubbles: true, detail }));

    const globalWindow = /** @type {any} */ (window);
    const candidates = [
      globalWindow.IOM,
      globalWindow.iom,
      globalWindow.IdealOfferManagement,
      globalWindow.__IOM__,
    ].filter(Boolean);
    const methodNames = ['refresh', 'rerender', 'recalculate', 'recalculatePrices', 'updatePrices', 'scan', 'applyDiscounts'];

    let didInvoke = false;
    for (const candidate of candidates) {
      for (const methodName of methodNames) {
        const maybeMethod = /** @type {any} */ (candidate)[methodName];
        if (typeof maybeMethod === 'function') {
          try {
            maybeMethod.call(candidate);
            didInvoke = true;
            break;
          } catch (_error) {
            // no-op: try the next compatible method name
          }
        }
      }
      if (didInvoke) break;
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
